import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface ComplianceCategory {
  key:        string;
  label:      string;
  weight:     number;
  pct:        number;      // 0-100
  applicable: boolean;      // false → excluida del cálculo y redistribuida
}

export interface ComplianceScoreResult {
  score:     number;        // 0-100, redondeado
  color:     string;
  loading:   boolean;
  breakdown: ComplianceCategory[];
}

const CATEGORY_WEIGHTS = {
  exams:       20,
  epp:         15,
  trainings:   15,
  inspections: 15,
  iper:        15,
  documents:   10,
  profile:     10,
};

const RENEWAL_MONTHS_HIGH_RISK = 6;
const RENEWAL_MONTHS_DEFAULT   = 12;
const IPER_RENEWAL_MONTHS      = 12;
const INSPECTION_GRACE_DAYS    = 90;  // sin penalización
const INSPECTION_DECAY_DAYS    = 90;  // días adicionales hasta llegar a 0%

export function scoreColor(score: number): string {
  if (score < 50) return '#ef4444';
  if (score < 80) return '#f59e0b';
  return '#22c55e';
}

function monthsBetween(from: Date, to: Date): number {
  return (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
}

function daysBetween(from: Date, to: Date): number {
  return Math.floor((to.getTime() - from.getTime()) / 86_400_000);
}

// ── Cálculo puro ─────────────────────────────────────────────────────────────

export async function computeComplianceScore(companyId: string): Promise<ComplianceScoreResult> {
  if (!companyId) {
    return { score: 0, color: scoreColor(0), loading: false, breakdown: [] };
  }

  const today = new Date();

  const [
    { data: workers },
    { data: eppAssignments },
    { data: iperRows },
    { data: ssoDocs },
    { data: ssoProfile },
    { data: trainings },
    { data: attendance },
    { data: inspectionRows },
  ] = await Promise.all([
    supabase.from('workers').select('id, risk_level, last_medical_exam').eq('company_id', companyId).eq('status', 'activo'),
    supabase.from('epp_assignments').select('worker_id, expiry_date, returned').eq('company_id', companyId).eq('returned', false),
    supabase.from('iper_assessments').select('fecha_evaluacion').eq('company_id', companyId).order('fecha_evaluacion', { ascending: false }).limit(1),
    supabase.from('sso_documents').select('expiry_date').eq('company_id', companyId).eq('is_current', true),
    supabase.from('company_sso_profile').select('workers_count, risk_level, company_size, sht_required, paritario_type').eq('company_id', companyId).maybeSingle(),
    supabase.from('sso_trainings').select('id, scheduled_date').eq('company_id', companyId),
    supabase.from('sso_training_attendance').select('training_id, worker_id, attended').eq('company_id', companyId).eq('attended', true),
    supabase.from('inspections').select('status, inspection_date').eq('company_id', companyId),
  ]);

  const activeWorkers = workers ?? [];
  const breakdown: ComplianceCategory[] = [];

  // ── Exámenes médicos vigentes ────────────────────────────────────────────────
  // Misma regla que HealthSurveillancePage: alto riesgo = renovación cada 6
  // meses, resto = 12 meses. "Vigente" = dentro del intervalo, sin contar
  // "por vencer" como cumplido.
  if (activeWorkers.length > 0) {
    const vigentes = activeWorkers.filter((w) => {
      if (!w.last_medical_exam) return false;
      const meses = w.risk_level === 'alto' ? RENEWAL_MONTHS_HIGH_RISK : RENEWAL_MONTHS_DEFAULT;
      const proxima = new Date(w.last_medical_exam);
      proxima.setMonth(proxima.getMonth() + meses);
      return proxima.getTime() >= today.getTime();
    }).length;
    breakdown.push({
      key: 'exams', label: 'Exámenes médicos vigentes', weight: CATEGORY_WEIGHTS.exams,
      pct: (vigentes / activeWorkers.length) * 100, applicable: true,
    });
  } else {
    breakdown.push({ key: 'exams', label: 'Exámenes médicos vigentes', weight: CATEGORY_WEIGHTS.exams, pct: 0, applicable: false });
  }

  // ── EPP sin vencer/asignado ──────────────────────────────────────────────────
  if (activeWorkers.length > 0) {
    const activeIds = new Set(activeWorkers.map((w) => w.id));
    const workersWithEpp = new Set(
      (eppAssignments ?? [])
        .filter((a) => activeIds.has(a.worker_id) && (!a.expiry_date || new Date(a.expiry_date) >= today))
        .map((a) => a.worker_id)
    ).size;
    breakdown.push({
      key: 'epp', label: 'EPP sin vencer/asignado', weight: CATEGORY_WEIGHTS.epp,
      pct: (workersWithEpp / activeWorkers.length) * 100, applicable: true,
    });
  } else {
    breakdown.push({ key: 'epp', label: 'EPP sin vencer/asignado', weight: CATEGORY_WEIGHTS.epp, pct: 0, applicable: false });
  }

  // ── Capacitaciones al día (por trabajador, últimos 12 meses) ────────────────
  if (activeWorkers.length > 0) {
    const oneYearAgo = new Date(today); oneYearAgo.setFullYear(today.getFullYear() - 1);
    const trainingDateById = new Map((trainings ?? []).map((t) => [t.id, t.scheduled_date]));
    const activeIds = new Set(activeWorkers.map((w) => w.id));
    const trainedRecently = new Set(
      (attendance ?? [])
        .filter((a) => {
          if (!activeIds.has(a.worker_id)) return false;
          const sched = trainingDateById.get(a.training_id);
          if (!sched) return false;
          return new Date(sched) >= oneYearAgo;
        })
        .map((a) => a.worker_id)
    ).size;
    breakdown.push({
      key: 'trainings', label: 'Capacitaciones al día', weight: CATEGORY_WEIGHTS.trainings,
      pct: (trainedRecently / activeWorkers.length) * 100, applicable: true,
    });
  } else {
    breakdown.push({ key: 'trainings', label: 'Capacitaciones al día', weight: CATEGORY_WEIGHTS.trainings, pct: 0, applicable: false });
  }

  // ── Inspecciones realizadas (si aplica) ──────────────────────────────────────
  // No aplica = la empresa nunca ha creado ninguna inspección → se excluye y
  // su peso se redistribuye entre las demás categorías.
  const hasAnyInspection = (inspectionRows ?? []).length > 0;
  if (hasAnyInspection) {
    const lastFinalizada = (inspectionRows ?? [])
      .filter((i) => i.status === 'finalizada')
      .sort((a, b) => b.inspection_date.localeCompare(a.inspection_date))[0];

    let pct = 0;
    if (lastFinalizada) {
      const dias = daysBetween(new Date(lastFinalizada.inspection_date), today);
      if (dias <= INSPECTION_GRACE_DAYS) pct = 100;
      else if (dias <= INSPECTION_GRACE_DAYS + INSPECTION_DECAY_DAYS) {
        pct = 100 - ((dias - INSPECTION_GRACE_DAYS) / INSPECTION_DECAY_DAYS) * 100;
      } else pct = 0;
    }
    breakdown.push({ key: 'inspections', label: 'Inspecciones realizadas', weight: CATEGORY_WEIGHTS.inspections, pct, applicable: true });
  } else {
    breakdown.push({ key: 'inspections', label: 'Inspecciones realizadas', weight: CATEGORY_WEIGHTS.inspections, pct: 0, applicable: false });
  }

  // ── IPER al día ───────────────────────────────────────────────────────────────
  // Siempre aplica: 0% si nunca se ha hecho, 50% si existe pero venció hace
  // más de 12 meses, 100% si está dentro del último año.
  const lastIper = iperRows?.[0];
  let iperPct = 0;
  if (lastIper?.fecha_evaluacion) {
    const meses = monthsBetween(new Date(lastIper.fecha_evaluacion), today);
    iperPct = meses <= IPER_RENEWAL_MONTHS ? 100 : 50;
  }
  breakdown.push({ key: 'iper', label: 'Matriz de Riesgos al día', weight: CATEGORY_WEIGHTS.iper, pct: iperPct, applicable: true });

  // ── Documentos SSO sin vencer ────────────────────────────────────────────────
  // Solo se evalúan los documentos vigentes (is_current=true) que tienen
  // fecha de vencimiento. Si ninguno la tiene, no hay nada vencido → 100%.
  const docsConVencimiento = (ssoDocs ?? []).filter((d) => d.expiry_date);
  const docsPct = docsConVencimiento.length === 0
    ? 100
    : (docsConVencimiento.filter((d) => new Date(d.expiry_date as string) >= today).length / docsConVencimiento.length) * 100;
  breakdown.push({ key: 'documents', label: 'Documentos SSO sin vencer', weight: CATEGORY_WEIGHTS.documents, pct: docsPct, applicable: true });

  // ── Perfil SSO completo ──────────────────────────────────────────────────────
  const PROFILE_FIELDS = ['workers_count', 'risk_level', 'company_size', 'sht_required', 'paritario_type'] as const;
  const filledFields = ssoProfile
    ? PROFILE_FIELDS.filter((f) => ssoProfile[f] !== null && ssoProfile[f] !== undefined && ssoProfile[f] !== '').length
    : 0;
  breakdown.push({
    key: 'profile', label: 'Perfil SSO completo', weight: CATEGORY_WEIGHTS.profile,
    pct: (filledFields / PROFILE_FIELDS.length) * 100, applicable: true,
  });

  // ── Score final (normalizado sobre el peso de las categorías aplicables) ────
  const applicableWeight = breakdown.filter((c) => c.applicable).reduce((s, c) => s + c.weight, 0);
  const earned = breakdown.filter((c) => c.applicable).reduce((s, c) => s + c.weight * (c.pct / 100), 0);
  const score = applicableWeight > 0 ? Math.round((earned / applicableWeight) * 100) : 0;

  return { score, color: scoreColor(score), loading: false, breakdown };
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useComplianceScore(companyId: string | null | undefined): ComplianceScoreResult {
  const [result, setResult] = useState<ComplianceScoreResult>({ score: 0, color: scoreColor(0), loading: true, breakdown: [] });

  const load = useCallback(async () => {
    if (!companyId) {
      setResult({ score: 0, color: scoreColor(0), loading: false, breakdown: [] });
      return;
    }
    setResult((prev) => ({ ...prev, loading: true }));
    const computed = await computeComplianceScore(companyId);
    setResult({ ...computed, loading: false });
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  return result;
}
